/**
 * =============================================================================
 * IDS CRM Web - Assets Page
 * =============================================================================
 *
 * PURPOSE:
 * Lists installed equipment with filtering, search, and wear part alerts.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_ASSET_CONFIG, getActiveOptions, getAssetCreatableOptions, getAssetTypeMeta, getOptionLabel, normaliseAssetConfig, resolveAssetImageSrc } from '../utils/assetTypes';
import { dateKeyOf, formatDateKey, toDateKey } from '../utils/dates';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Assets.css';

function Assets() {
  const { authFetch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Coming back from an asset (a "←" link or the browser's Back) shows the
  // list as it was left; a fresh visit starts from the address.
  const memory = useListMemory('/assets');

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(() => ({ page: memory.initial('page', 1), total: 0, totalPages: 0 }));
  const [search, setSearch] = useState(() => memory.initial('search', searchParams.get('search') || ''));
  const [statusFilter, setStatusFilter] = useState(() => memory.initial('status', searchParams.get('status') || ''));
  const [typeFilter, setTypeFilter] = useState(() => memory.initial('type', searchParams.get('type') || ''));
  const [activeTab, setActiveTab] = useState(() => memory.initial('tab', searchParams.get('tab') || 'all'));
  const [sortColumn, setSortColumn] = useState(() => memory.initial('sort', searchParams.get('sort') || 'created_at'));
  const [sortDirection, setSortDirection] = useState(() => memory.initial('dir', searchParams.get('dir') || 'desc'));
  // The customer filter lives in the address only, so it comes back with it.
  const customerId = searchParams.get('customer_id') || '';
  const [wearAlerts, setWearAlerts] = useState([]);
  const [dueService, setDueService] = useState([]);
  const [assetConfig, setAssetConfig] = useState(DEFAULT_ASSET_CONFIG);
  const [summary, setSummary] = useState({ by_type: [], total: 0 });

  const robotTypes = [{ value: '', label: 'All Types' }, ...getAssetCreatableOptions(assetConfig.asset_types)];
  const statuses = getActiveOptions(assetConfig.statuses);

  useRememberList('/assets', {
    search,
    status: statusFilter,
    type: typeFilter,
    tab: activeTab,
    sort: sortColumn,
    dir: sortDirection,
    page: pagination.page
  });

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    let steppingBack = false;
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: '25'
      });

      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('robot_type', typeFilter);
      if (customerId) params.append('customer_id', customerId);
      if (sortColumn) params.append('sort', sortColumn);
      if (sortDirection) params.append('dir', sortDirection);

      const response = await authFetch(`/api/v1/assets?${params}`);
      const data = await response.json();
      // A page past the end (the list got shorter while an asset was open,
      // and the page was restored) steps back to the last page there is. The
      // list stays loading until that page arrives, so it never reads as
      // empty. Only if the list is still on the page this reply was for.
      const lastPage = Math.max(1, data.pagination?.totalPages || 0);
      if (response.ok && data.pagination && pagination.page > lastPage) {
        steppingBack = true;
        setPagination(prev => (prev.page === pagination.page ? { ...prev, page: lastPage } : prev));
        return;
      }
      setAssets(data.data || []);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      if (!steppingBack) setLoading(false);
    }
  }, [authFetch, pagination.page, search, statusFilter, typeFilter, customerId, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const SortHeader = ({ column, children }) => (
    <th onClick={() => handleSort(column)} className="sortable-header">
      {children}
      {sortColumn === column && (
        <span className="sort-indicator">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>
      )}
    </th>
  );

  const fetchAlerts = useCallback(async () => {
    try {
      const [wearRes, serviceRes] = await Promise.all([
        authFetch('/api/v1/assets/wear-alerts?days=14'),
        authFetch('/api/v1/assets/due-service?days=30')
      ]);

      const wearData = await wearRes.json();
      const serviceData = await serviceRes.json();

      setWearAlerts(wearData.data || []);
      setDueService(serviceData.data || []);
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  }, [authFetch]);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchAssets();
    }
  }, [activeTab, fetchAssets]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    const fetchAssetConfig = async () => {
      try {
        const [configRes, summaryRes] = await Promise.all([
          authFetch('/api/v1/assets/config'),
          authFetch('/api/v1/assets/summary')
        ]);

        if (configRes.ok) {
          const data = await configRes.json();
          setAssetConfig(normaliseAssetConfig(data.data));
        }

        if (summaryRes.ok) {
          const summaryData = await summaryRes.json();
          setSummary(summaryData.data || { by_type: [], total: 0 });
        }
      } catch (error) {
        console.error('Failed to fetch asset config:', error);
      }
    };

    fetchAssetConfig();
  }, [authFetch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    const newParams = new URLSearchParams(searchParams);
    if (search) {
      newParams.set('search', search);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  // Install and due dates are DATE columns: calendar days in every zone.
  const formatDate = (date) => formatDateKey(date);

  // Due dates are compared as days against the viewer's today, not as
  // instants: new Date(due) is UTC midnight, so maintenance due today showed
  // overdue from 01:00 BST, and from the evening before in New York.
  // Maintenance is overdue once its day has passed; a wear part is flagged
  // from its due day.
  const isPastDue = (date) => {
    const key = dateKeyOf(date);
    return Boolean(key) && key < toDateKey(new Date());
  };

  const isDueByToday = (date) => {
    const key = dateKeyOf(date);
    return Boolean(key) && key <= toDateKey(new Date());
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending_install': return 'status-pending-install';
      case 'active': return 'status-active';
      case 'fault': return 'status-fault';
      case 'offline': return 'status-offline';
      case 'decommissioned': return 'status-decommissioned';
      case 'in_warranty': return 'status-warranty';
      default: return '';
    }
  };

  const getHealthClass = (health) => {
    switch (health) {
      case 'green': return 'health-green';
      case 'amber': return 'health-amber';
      case 'red': return 'health-red';
      default: return '';
    }
  };

  return (
    <div className="assets-page">
      <div className="page-header">
        <div>
          <h1>Assets</h1>
          <p>Installed equipment and wear tracking</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/assets/components-pool" className="btn-secondary">
            Workshop &amp; Pool
          </Link>
          <Link to="/assets/new" className="btn-primary">
            + New Asset
          </Link>
        </div>
      </div>

      {summary.total > 0 && (
        <div className="asset-summary">
          <div className="summary-total">
            <span className="summary-count">{summary.total}</span>
            <span className="summary-label">Total Assets</span>
          </div>
          <div className="summary-types">
            {summary.by_type.map(item => {
              const typeMeta = getAssetTypeMeta(item.robot_type, assetConfig);
              return (
                <button
                  key={item.robot_type}
                  className={`summary-type-chip ${typeFilter === item.robot_type ? 'active' : ''}`}
                  onClick={() => {
                    setTypeFilter(typeFilter === item.robot_type ? '' : item.robot_type);
                    setPagination(prev => ({ ...prev, page: 1 }));
                    setActiveTab('all');
                  }}
                >
                  <img src={resolveAssetImageSrc(typeMeta.image)} alt="" className="summary-type-icon" />
                  <span className="summary-type-label">{typeMeta.label}</span>
                  <span className="summary-type-count">{item.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="assets-tabs">
        <button
          className={activeTab === 'all' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('all')}
        >
          All Assets
        </button>
        <button
          className={activeTab === 'service' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('service')}
        >
          Due Service
          {dueService.length > 0 && <span className="tab-badge">{dueService.length}</span>}
        </button>
        <button
          className={activeTab === 'wear' ? 'tab active' : 'tab'}
          onClick={() => handleTabChange('wear')}
        >
          Wear Alerts
          {wearAlerts.length > 0 && <span className="tab-badge alert">{wearAlerts.length}</span>}
        </button>
      </div>

      {activeTab === 'all' && (
        <>
          <div className="assets-filters">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                placeholder="Search by serial number, ref, or customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-btn">Search</button>
            </form>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              {robotTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="filter-select"
            >
              {statuses.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="loading">Loading assets...</div>
          ) : (
            <>
              <div className="assets-table">
                <table>
                  <thead>
                    <tr>
                      <SortHeader column="asset_ref">Ref</SortHeader>
                      <SortHeader column="serial_number">Serial Number</SortHeader>
                      <SortHeader column="robot_type">Type</SortHeader>
                      <SortHeader column="customer_name">Customer</SortHeader>
                      <SortHeader column="site_name">Farm Site</SortHeader>
                      <SortHeader column="status">Status</SortHeader>
                      <SortHeader column="health_flag">Health</SortHeader>
                      <SortHeader column="install_date">Install Date</SortHeader>
                      <th>Next Maintenance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="empty-row">No assets found</td>
                      </tr>
                    ) : (
                      assets.map(asset => {
                        const type = getAssetTypeMeta(asset.robot_type, assetConfig);
                        return (
                        <tr key={asset.asset_id}>
                          <td>
                            <Link to={`/assets/${asset.asset_id}`} className="asset-ref">
                              {asset.asset_ref}
                            </Link>
                          </td>
                          <td className="serial-number">{asset.serial_number}</td>
                          <td>
                            <div className="asset-type-cell">
                              <img src={resolveAssetImageSrc(type.image)} alt="" className="asset-type-thumb" />
                              <span>{type.label}</span>
                            </div>
                          </td>
                          <td>
                            <Link to={`/customers/${asset.customer_id}`} className="customer-link">
                              {asset.customer_name}
                            </Link>
                          </td>
                          <td>{asset.site_name || 'Unassigned'}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(asset.status)}`}>
                              {getOptionLabel(assetConfig.statuses, asset.status)}
                            </span>
                          </td>
                          <td>
                            <span className={`health-dot ${getHealthClass(asset.health_flag)}`} />
                          </td>
                          <td>{formatDate(asset.install_date)}</td>
                          <td className={isPastDue(asset.next_maintenance_due) ? 'overdue' : ''}>
                            {asset.next_maintenance_due ? formatDate(asset.next_maintenance_due) : '-'}
                          </td>
                        </tr>
                      );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Previous
                  </button>
                  <span>Page {pagination.page} of {pagination.totalPages}</span>
                  <button
                    disabled={pagination.page === pagination.totalPages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'service' && (
        <div className="service-due-list">
          <div className="list-header">
            <h3>Assets Due for Service (Next 30 Days)</h3>
          </div>
          {dueService.length === 0 ? (
            <div className="empty-state">No assets due for service</div>
          ) : (
            <div className="alert-cards">
              {dueService.map(asset => {
                const type = getAssetTypeMeta(asset.robot_type, assetConfig);
                return (
                <Link key={asset.asset_id} to={`/assets/${asset.asset_id}`} className="alert-card service">
                  <div className="alert-header">
                    <span className="asset-ref">{asset.asset_ref}</span>
                    <span className={`days-badge ${asset.days_until_due <= 0 ? 'overdue' : asset.days_until_due <= 7 ? 'soon' : ''}`}>
                      {asset.days_until_due <= 0 ? 'Overdue' : `${asset.days_until_due} days`}
                    </span>
                  </div>
                  <div className="alert-body">
                    <div className="alert-type-row">
                      <img src={resolveAssetImageSrc(type.image)} alt="" className="asset-type-thumb" />
                      <span className="alert-type">{type.label}</span>
                    </div>
                    <div className="alert-customer">{asset.customer_name}</div>
                    <div className="alert-date">Due: {formatDate(asset.next_service_due || asset.next_maintenance_due)}</div>
                  </div>
                </Link>
              );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'wear' && (
        <div className="wear-alerts-list">
          <div className="list-header">
            <h3>Wear Part Replacement Alerts</h3>
          </div>
          {wearAlerts.length === 0 ? (
            <div className="empty-state">No wear part replacements due</div>
          ) : (
            <div className="alert-cards">
              {wearAlerts.map(asset => {
                const type = getAssetTypeMeta(asset.robot_type, assetConfig);
                return (
                <Link key={asset.asset_id} to={`/assets/${asset.asset_id}`} className="alert-card wear">
                  <div className="alert-header">
                    <span className="asset-ref">{asset.asset_ref}</span>
                    <span className="wear-type-badge">{asset.alert_type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="alert-body">
                    <div className="alert-type-row">
                      <img src={resolveAssetImageSrc(type.image)} alt="" className="asset-type-thumb" />
                      <span className="alert-type">{type.label}</span>
                    </div>
                    <div className="alert-customer">{asset.customer_name}</div>
                    <div className="wear-dates">
                      {asset.brush_replacement_due && (
                        <div className={isDueByToday(asset.brush_replacement_due) ? 'overdue' : ''}>
                          Brush: {formatDate(asset.brush_replacement_due)}
                        </div>
                      )}
                      {asset.nozzle_replacement_due && (
                        <div className={isDueByToday(asset.nozzle_replacement_due) ? 'overdue' : ''}>
                          Nozzle: {formatDate(asset.nozzle_replacement_due)}
                        </div>
                      )}
                      {asset.drive_belt_due && (
                        <div className={isDueByToday(asset.drive_belt_due) ? 'overdue' : ''}>
                          Belt: {formatDate(asset.drive_belt_due)}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Assets;
